import { TaskAttentionClock } from "../store/taskAttention"
import { observable, observe } from "mobx"
import { retryBusy } from "../nest/appDataRetry"
import { parseProjectChars, normalizeProjectEmoji } from "../projectAppearance"
import {
    NestClientError,
    type NestMethodParams,
    type NestMethodResult,
    type NestNotificationParams,
    type NestSubscriptionFilter,
    nestClient,
    requestNestMethod,
} from "../nest/client"
import {
    REPOS_NAMESPACE,
    type RepoRecord,
    TASKS_NAMESPACE,
    type TaskRecord,
    type TaskRecordWriteSource,
    type TaskSessionRef,
    parseRepoRecord,
    parseTaskRecord,
    repoIdFromRecordKey,
    repoRecordKey,
    taskIdFromRecordKey,
    taskRecordKey,
    taskRecordForWrite,
    taskRecordWriteSource,
    taskSessionIdentity,
} from "./records"

type ProductStateMethod = "appData.list" | "appData.get" | "appData.set" | "appData.delete"
export type ProductStateRequest = <M extends ProductStateMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface ProductStateSubscriptionClient {
    retainSubscriptions(filters: NestSubscriptionFilter[]): Promise<() => void>
    on(method: "appData.changed", handler: (params: NestNotificationParams<"appData.changed"> | undefined) => void): () => void
}

export interface ProductStateStore {
    load(): Promise<void>
    dispose(): void
    readonly repos: ReadonlyMap<string, RepoRecord>
    readonly attentionNow?: number
    readonly tasks: ReadonlyMap<string, TaskRecord>
    refreshTask(id: string, workspaceId: string): Promise<void>
    refreshRepo(id: string, workspaceId: string): Promise<void>
    putRepo(record: RepoRecord, options?: { identityIntent: "fill-missing" }): Promise<void>
    putTask(record: TaskRecord, options?: TaskWriteOptions): Promise<void>
    deleteRepo(id: string): Promise<void>
    deleteTask(id: string): Promise<void>
    acceptTaskDeleted(id: string): void
    onRecordChanged(handler: (namespace: string, key: string) => void): () => void
}

export interface TaskWriteOptions {
    titleIntent?: "manual"
    lifecycle?: { closed: boolean; closedAt?: string }
}

interface ProductStateStoreOptions {
    request?: ProductStateRequest
    client?: ProductStateSubscriptionClient
    debounceMs?: number
    workspaceId?: string
}

type ProductNamespace = typeof REPOS_NAMESPACE | typeof TASKS_NAMESPACE
type ProductRecord = RepoRecord | TaskRecord
type TaskTitleWriteIntent = "preserve" | TaskWriteOptions["titleIntent"]

function fillMissingProjectIdentity(current: RepoRecord, generated: RepoRecord): RepoRecord {
    return {
        ...current,
        projectChars: current.projectChars ?? parseProjectChars(current.glyph) ?? generated.projectChars,
        projectEmoji: current.projectEmoji || normalizeProjectEmoji(current.glyph ?? "") || generated.projectEmoji,
    }
}

type PendingOperation =
    | { type: "set"; namespace: typeof REPOS_NAMESPACE; key: string; workspaceId: string; record: RepoRecord; identityIntent?: "fill-missing" }
    | {
          type: "set"
          namespace: typeof TASKS_NAMESPACE
          key: string
          workspaceId: string
          record: TaskRecord
          titleIntent: TaskTitleWriteIntent
          lifecycle?: TaskWriteOptions["lifecycle"]
      }
    | { type: "delete"; namespace: ProductNamespace; key: string; workspaceId: string }

interface PendingWrite {
    operation: PendingOperation
    waiters: Array<{ resolve: () => void; reject: (error: unknown) => void }>
    timer: ReturnType<typeof globalThis.setTimeout> | null
    flushing: boolean
}

const DEFAULT_PAGE_LIMIT = 5000
const DEFAULT_WRITE_DEBOUNCE_MS = 25

const defaultRequest: ProductStateRequest = (method, params) => requestNestMethod(method, params)

function versionKey(namespace: ProductNamespace, key: string, workspaceId: string): string {
    return `${namespace}\0${workspaceId}\0${key}`
}

function pendingKey(namespace: ProductNamespace, key: string, workspaceId: string): string {
    return versionKey(namespace, key, workspaceId)
}

function taskSourceKey(taskId: string, workspaceId: string): string {
    return `${workspaceId}\0${taskId}`
}

function recordWorkspaceId(record: ProductRecord): string {
    return record.workspaceId
}

function recordForNamespace(namespace: ProductNamespace, value: unknown): ProductRecord | null {
    return namespace === REPOS_NAMESPACE ? parseRepoRecord(value) : parseTaskRecord(value)
}

function isRepoRecord(record: ProductRecord): record is RepoRecord {
    return "path" in record
}

function isTaskRecord(record: ProductRecord): record is TaskRecord {
    return "sessions" in record
}

function applyTaskTitleWriteIntent(record: TaskRecord, current: TaskRecord | undefined, intent: TaskTitleWriteIntent): TaskRecord {
    if (intent === "manual") {
        return {
            ...record,
            titleManuallySet: true,
            titleAgentInitialized: true,
        }
    }
    if (!current) return record

    const next = { ...record, title: current.title }
    if (current.titleManuallySet === undefined) delete next.titleManuallySet
    else next.titleManuallySet = current.titleManuallySet
    if (current.titleAgentInitialized === undefined) delete next.titleAgentInitialized
    else next.titleAgentInitialized = current.titleAgentInitialized
    return next
}

// PR links/policy belong to the backend. Only an explicit Close/Reopen may change
// list placement; ordinary metadata writes must also preserve backend deletions.
function applyTaskLifecycleWriteIntent(record: TaskRecord, current: TaskRecord | undefined, lifecycle?: TaskWriteOptions["lifecycle"]): TaskRecord {
    if (!current) return record
    return {
        ...record,
        externalLinks: current.externalLinks,
        pullRequestAutomation: current.pullRequestAutomation,
        attention: current.attention,
        pullRequestWait: current.pullRequestWait,
        closed: lifecycle ? lifecycle.closed : current.closed,
        closedAt: lifecycle ? lifecycle.closedAt : current.closedAt,
    }
}

function applyTaskSessionWriteIntent(record: TaskRecord, current: TaskRecord | undefined): TaskRecord {
    if (!current) return record

    const localByIdentity = new Map<string, TaskSessionRef>()
    for (const session of record.sessions) {
        const identity = taskSessionIdentity(session)
        if (identity) localByIdentity.set(identity, session)
    }
    const mergedSessions = current.sessions.map((currentSession) => {
        const identity = taskSessionIdentity(currentSession)
        const localSession = identity ? localByIdentity.get(identity) : undefined
        if (!identity || !localSession) return currentSession
        localByIdentity.delete(identity)
        return {
            ...localSession,
            ...currentSession,
            updatedAt: latestTimestamp(localSession.updatedAt, currentSession.updatedAt),
        }
    })
    for (const localSession of record.sessions) {
        const identity = taskSessionIdentity(localSession)
        if (!identity || localByIdentity.delete(identity)) mergedSessions.push(localSession)
    }
    return {
        ...record,
        sessions: mergedSessions,
        activeSessionId: current.activeSessionId,
        handoff: current.handoff,
        updatedAt: latestTimestamp(record.updatedAt, current.updatedAt) ?? record.updatedAt,
    }
}

function latestTimestamp(first: string | undefined, second: string | undefined): string | undefined {
    if (!first) return second
    if (!second) return first
    const firstTime = Date.parse(first)
    const secondTime = Date.parse(second)
    if (!Number.isFinite(firstTime) || !Number.isFinite(secondTime)) return second
    return firstTime >= secondTime ? first : second
}

function idFromKey(namespace: ProductNamespace, key: string): string | null {
    return namespace === REPOS_NAMESPACE ? repoIdFromRecordKey(key) : taskIdFromRecordKey(key)
}

export class NestProductStateStore implements ProductStateStore {
    readonly repos = observable.map<string, RepoRecord>()
    readonly tasks = observable.map<string, TaskRecord>()
    private readonly attentionClock = new TaskAttentionClock()
    private readonly attentionDisposer = observe(this.tasks, (change) => {
        if (
            change.type === "update" &&
            change.oldValue.closed === change.newValue.closed &&
            change.oldValue.attention?.sleepUntil === change.newValue.attention?.sleepUntil
        )
            return
        this.attentionClock.observe(change.name, change.type === "delete" ? undefined : change.newValue)
    })
    get attentionNow(): number {
        return this.attentionClock.now
    }

    private readonly request: ProductStateRequest
    private readonly client: ProductStateSubscriptionClient
    private readonly debounceMs: number
    private readonly workspaceId?: string
    private readonly versions = new Map<string, number>()
    private readonly taskSources = new Map<string, TaskRecordWriteSource>()
    private readonly pendingWrites = new Map<string, PendingWrite>()
    private readonly recordChangedHandlers = new Set<(namespace: string, key: string) => void>()
    private notificationDisposer: (() => void) | null = null
    private subscriptionDisposer: (() => void) | null = null
    private loadPromise: Promise<void> | null = null
    private bufferedNotifications: Map<string, NestNotificationParams<"appData.changed">> | null = null

    constructor(options: ProductStateStoreOptions = {}) {
        this.request = options.request ?? defaultRequest
        this.client = options.client ?? nestClient
        this.debounceMs = options.debounceMs ?? DEFAULT_WRITE_DEBOUNCE_MS
        this.workspaceId = options.workspaceId
    }

    async load(): Promise<void> {
        if (this.loadPromise) return this.loadPromise
        const load = this.loadSnapshot()
        this.loadPromise = load
        try {
            await load
        } finally {
            if (this.loadPromise === load) this.loadPromise = null
        }
    }

    private async loadSnapshot(): Promise<void> {
        const hadSubscription = this.subscriptionDisposer !== null
        const bufferedNotifications = new Map<string, NestNotificationParams<"appData.changed">>()
        this.bufferedNotifications = bufferedNotifications
        try {
            await this.ensureNotificationSubscription()
        } catch (error) {
            this.bufferedNotifications = null
            throw error
        }

        try {
            const [repos, tasks] = await Promise.all([this.listRecords(REPOS_NAMESPACE), this.listRecords(TASKS_NAMESPACE)])

            this.repos.clear()
            for (const repo of repos) {
                if (isRepoRecord(repo)) this.repos.set(repo.id, repo)
            }
            this.tasks.clear()
            for (const task of tasks) {
                if (isTaskRecord(task)) this.tasks.set(task.id, task)
            }

            while (bufferedNotifications.size > 0) {
                const buffered = [...bufferedNotifications.values()]
                bufferedNotifications.clear()
                for (const params of buffered) await this.handleAppDataChanged(params)
            }
            this.bufferedNotifications = null
        } catch (error) {
            this.bufferedNotifications = null
            if (!hadSubscription) this.stopNotificationSubscription()
            throw error
        }
    }

    dispose(): void {
        this.attentionDisposer()
        this.attentionClock.dispose()
        this.stopNotificationSubscription()
        this.bufferedNotifications = null
        for (const pending of this.pendingWrites.values()) {
            if (pending.timer) globalThis.clearTimeout(pending.timer)
            for (const waiter of pending.waiters) waiter.reject(new Error("ProductStateStore disposed"))
        }
        this.pendingWrites.clear()
        this.taskSources.clear()
        this.recordChangedHandlers.clear()
    }

    private async ensureNotificationSubscription(): Promise<void> {
        if (this.subscriptionDisposer) return
        this.notificationDisposer = this.client.on("appData.changed", (params) => {
            if (params && this.bufferedNotifications) {
                this.bufferedNotifications.set(`${params.namespace}\0${params.workspaceId}\0${params.key}`, params)
                return
            }
            void this.handleAppDataChanged(params).catch((error) => {
                console.warn("[ProductStateStore] Failed to apply appData notification:", error)
            })
        })
        try {
            this.subscriptionDisposer = await this.client.retainSubscriptions(this.productStateSubscriptions())
        } catch (error) {
            this.notificationDisposer?.()
            this.notificationDisposer = null
            throw error
        }
    }

    private stopNotificationSubscription(): void {
        this.notificationDisposer?.()
        this.notificationDisposer = null
        this.subscriptionDisposer?.()
        this.subscriptionDisposer = null
    }

    putRepo(record: RepoRecord, options?: { identityIntent: "fill-missing" }): Promise<void> {
        const parsed = parseRepoRecord(record)
        if (!parsed) return Promise.reject(new Error("Invalid repo record"))
        const pending = this.pendingWrites.get(pendingKey(REPOS_NAMESPACE, repoRecordKey(parsed.id), parsed.workspaceId))
        if (options?.identityIntent === "fill-missing" && pending) {
            // Do not coalesce a user's metadata write into a background fill-only write.
            return new Promise<void>((resolve, reject) => pending.waiters.push({ resolve, reject })).then(() => {
                const current = this.repos.get(parsed.id)
                if (!current) throw new Error("Project was removed before its identity could be saved")
                return this.putRepo(fillMissingProjectIdentity(current, parsed), options)
            })
        }
        this.repos.set(parsed.id, parsed)
        this.emitRecordChanged(REPOS_NAMESPACE, repoRecordKey(parsed.id))
        return this.queueWrite({
            type: "set",
            namespace: REPOS_NAMESPACE,
            key: repoRecordKey(parsed.id),
            workspaceId: parsed.workspaceId,
            record: parsed,
            ...(options?.identityIntent ? { identityIntent: options.identityIntent } : {}),
        })
    }

    putTask(record: TaskRecord, options?: TaskWriteOptions): Promise<void> {
        const parsed = parseTaskRecord(record)
        if (!parsed) return Promise.reject(new Error("Invalid task record"))
        const titleIntent = options?.titleIntent ?? "preserve"
        const pending = this.pendingWrites.get(pendingKey(TASKS_NAMESPACE, taskRecordKey(parsed.id), parsed.workspaceId))
        const queuedLifecycle =
            pending && !pending.flushing && pending.operation.type === "set" && pending.operation.namespace === TASKS_NAMESPACE
                ? pending.operation.lifecycle
                : undefined
        const lifecycle = options?.lifecycle ?? queuedLifecycle
        const current = this.tasks.get(parsed.id)
        const next = applyTaskLifecycleWriteIntent(applyTaskTitleWriteIntent(parsed, current, titleIntent), current, lifecycle)
        this.tasks.set(next.id, next)
        this.emitRecordChanged(TASKS_NAMESPACE, taskRecordKey(next.id))
        return this.queueWrite({
            type: "set",
            namespace: TASKS_NAMESPACE,
            key: taskRecordKey(next.id),
            workspaceId: next.workspaceId,
            record: next,
            titleIntent,
            lifecycle,
        })
    }

    async refreshTask(id: string, workspaceId: string): Promise<void> {
        await this.refetchRecord(TASKS_NAMESPACE, taskRecordKey(id), workspaceId)
        this.emitRecordChanged(TASKS_NAMESPACE, taskRecordKey(id))
    }

    async refreshRepo(id: string, workspaceId: string): Promise<void> {
        await this.refetchRecord(REPOS_NAMESPACE, repoRecordKey(id), workspaceId)
        this.emitRecordChanged(REPOS_NAMESPACE, repoRecordKey(id))
    }

    deleteRepo(id: string): Promise<void> {
        const existing = this.repos.get(id)
        if (!existing) return Promise.resolve()
        this.repos.delete(id)
        this.emitRecordChanged(REPOS_NAMESPACE, repoRecordKey(id))
        return this.queueWrite({ type: "delete", namespace: REPOS_NAMESPACE, key: repoRecordKey(id), workspaceId: existing.workspaceId })
    }

    deleteTask(id: string): Promise<void> {
        const existing = this.tasks.get(id)
        if (!existing) return Promise.resolve()
        this.tasks.delete(id)
        this.taskSources.delete(taskSourceKey(id, existing.workspaceId))
        this.emitRecordChanged(TASKS_NAMESPACE, taskRecordKey(id))
        return this.queueWrite({ type: "delete", namespace: TASKS_NAMESPACE, key: taskRecordKey(id), workspaceId: existing.workspaceId })
    }

    acceptTaskDeleted(id: string): void {
        const existing = this.tasks.get(id)
        if (!existing) return
        const key = taskRecordKey(id)
        this.cancelPendingWrite(TASKS_NAMESPACE, key, existing.workspaceId)
        this.tasks.delete(id)
        this.taskSources.delete(taskSourceKey(id, existing.workspaceId))
        this.versions.delete(versionKey(TASKS_NAMESPACE, key, existing.workspaceId))
        this.emitRecordChanged(TASKS_NAMESPACE, key)
    }

    onRecordChanged(handler: (namespace: string, key: string) => void): () => void {
        this.recordChangedHandlers.add(handler)
        return () => {
            this.recordChangedHandlers.delete(handler)
        }
    }

    private async listRecords(namespace: ProductNamespace): Promise<ProductRecord[]> {
        const records: ProductRecord[] = []
        let cursor: string | undefined
        const prefix = namespace === REPOS_NAMESPACE ? "repo." : "task."

        for (;;) {
            const result = await retryBusy(() =>
                this.request("appData.list", {
                    namespace,
                    prefix,
                    limit: DEFAULT_PAGE_LIMIT,
                    ...(this.workspaceId ? { scope: { workspaceId: this.workspaceId } } : {}),
                    ...(cursor ? { cursor } : {}),
                })
            )

            for (const item of result.items) {
                const parsed = recordForNamespace(namespace, item.value)
                const id = idFromKey(namespace, item.key)
                if (!parsed || !id || parsed.id !== id || parsed.workspaceId !== item.scope.workspaceId) continue
                records.push(parsed)
                if (isTaskRecord(parsed)) this.rememberTaskSource(parsed, item.value)
                this.versions.set(versionKey(namespace, item.key, parsed.workspaceId), item.version)
            }

            if (!result.hasMore || !result.nextCursor) return records
            cursor = result.nextCursor
        }
    }

    private productStateSubscriptions(): NestSubscriptionFilter[] {
        const appDataScope = this.workspaceId ? { workspaceId: this.workspaceId } : undefined
        return [
            { method: "appData.changed", appDataNamespace: REPOS_NAMESPACE, ...(appDataScope ? { appDataScope } : {}) },
            { method: "appData.changed", appDataNamespace: TASKS_NAMESPACE, ...(appDataScope ? { appDataScope } : {}) },
        ]
    }

    private queueWrite(operation: PendingOperation): Promise<void> {
        const key = pendingKey(operation.namespace, operation.key, operation.workspaceId)
        let pending = this.pendingWrites.get(key)
        if (!pending) {
            pending = {
                operation,
                waiters: [],
                timer: null,
                flushing: false,
            }
            this.pendingWrites.set(key, pending)
        }

        pending.operation = operation
        const result = new Promise<void>((resolve, reject) => {
            pending?.waiters.push({ resolve, reject })
        })

        if (!pending.flushing && !pending.timer) {
            pending.timer = globalThis.setTimeout(() => {
                void this.flushPendingWrite(key)
            }, this.debounceMs)
        }

        return result
    }

    private async flushPendingWrite(key: string): Promise<void> {
        const pending = this.pendingWrites.get(key)
        if (!pending || pending.flushing) return

        if (pending.timer) {
            globalThis.clearTimeout(pending.timer)
            pending.timer = null
        }

        const operation = pending.operation
        const waiters = pending.waiters
        pending.waiters = []
        pending.flushing = true

        try {
            await this.applyOperation(operation)
            for (const waiter of waiters) waiter.resolve()
        } catch (error) {
            for (const waiter of waiters) waiter.reject(error)
        } finally {
            pending.flushing = false
            if (pending.waiters.length > 0) {
                pending.timer = globalThis.setTimeout(() => {
                    void this.flushPendingWrite(key)
                }, this.debounceMs)
            } else {
                this.pendingWrites.delete(key)
            }
        }
    }

    private async applyOperation(operation: PendingOperation): Promise<void> {
        if (operation.type === "delete") {
            await retryBusy(() =>
                this.request("appData.delete", {
                    namespace: operation.namespace,
                    key: operation.key,
                    scope: { workspaceId: operation.workspaceId },
                })
            )
            this.versions.delete(versionKey(operation.namespace, operation.key, operation.workspaceId))
            return
        }

        if (operation.namespace === TASKS_NAMESPACE) {
            const current = this.tasks.get(operation.record.id)
            const titledRecord = applyTaskTitleWriteIntent(operation.record, current, operation.titleIntent)
            const record = applyTaskLifecycleWriteIntent(applyTaskSessionWriteIntent(titledRecord, current), current, operation.lifecycle)
            const value = taskRecordForWrite(record, this.taskSources.get(taskSourceKey(record.id, record.workspaceId)))
            this.setLocalRecord(operation.namespace, record)
            await this.setRecord(
                operation.namespace,
                operation.key,
                operation.workspaceId,
                record,
                value,
                true,
                operation.titleIntent,
                undefined,
                operation.lifecycle
            )
            return
        }
        let record = operation.record
        if (operation.identityIntent === "fill-missing") {
            // Notifications can advance the cached record/version while this write is debounced.
            const current = this.repos.get(record.id)
            if (!current) throw new Error("Project was removed before its identity could be saved")
            record = fillMissingProjectIdentity(current, record)
            this.setLocalRecord(operation.namespace, record)
        }
        await this.setRecord(operation.namespace, operation.key, operation.workspaceId, record, record, true, undefined, operation.identityIntent)
    }

    private async setRecord(
        namespace: ProductNamespace,
        key: string,
        workspaceId: string,
        record: ProductRecord,
        value: Record<string, unknown>,
        retryConflict: boolean,
        taskTitleIntent?: TaskTitleWriteIntent,
        identityIntent?: "fill-missing",
        lifecycle?: TaskWriteOptions["lifecycle"]
    ): Promise<void> {
        const expectedVersion = this.versions.get(versionKey(namespace, key, workspaceId))
        try {
            const result = await retryBusy(() =>
                this.request("appData.set", {
                    namespace,
                    key,
                    scope: { workspaceId },
                    value,
                    ...(expectedVersion !== undefined ? { expectedVersion } : {}),
                })
            )
            this.versions.set(versionKey(namespace, key, workspaceId), result.version)
            if (isTaskRecord(record)) this.rememberTaskSource(record, value)
        } catch (error) {
            if (!retryConflict || !(error instanceof NestClientError) || error.code !== "version_conflict") throw error
            const current = await this.refetchRecord(namespace, key, workspaceId)
            if (!current) throw new Error(`The ${namespace} record was deleted while a local write was in flight`)
            const parsedMerged = recordForNamespace(
                namespace,
                identityIntent === "fill-missing" && isRepoRecord(current) && isRepoRecord(record)
                    ? fillMissingProjectIdentity(current, record)
                    : { ...current, ...record }
            )
            if (!parsedMerged) throw new Error(`Invalid ${namespace} record after version_conflict reapply`)
            const merged =
                isTaskRecord(parsedMerged) && isTaskRecord(current)
                    ? applyTaskLifecycleWriteIntent(
                          applyTaskSessionWriteIntent(applyTaskTitleWriteIntent(parsedMerged, current, taskTitleIntent ?? "preserve"), current),
                          current,
                          lifecycle
                      )
                    : parsedMerged
            const mergedValue = isTaskRecord(merged) ? taskRecordForWrite(merged, this.taskSources.get(taskSourceKey(merged.id, merged.workspaceId))) : merged
            this.setLocalRecord(namespace, merged)
            await this.setRecord(namespace, key, workspaceId, merged, mergedValue, false, taskTitleIntent, identityIntent, lifecycle)
        }
    }

    private async handleAppDataChanged(params: NestNotificationParams<"appData.changed"> | undefined): Promise<void> {
        if (!params || (params.namespace !== REPOS_NAMESPACE && params.namespace !== TASKS_NAMESPACE)) return
        const namespace = params.namespace
        const workspaceId = params.workspaceId
        const id = idFromKey(namespace, params.key)
        if (!workspaceId || !id) return

        if (params.deleted) {
            this.cancelPendingWrite(namespace, params.key, workspaceId)
            this.deleteLocalRecord(namespace, id)
            this.versions.delete(versionKey(namespace, params.key, workspaceId))
        } else {
            await this.refetchRecord(namespace, params.key, workspaceId)
        }

        this.emitRecordChanged(namespace, params.key)
    }

    private emitRecordChanged(namespace: ProductNamespace, key: string): void {
        for (const handler of this.recordChangedHandlers) handler(namespace, key)
    }

    private cancelPendingWrite(namespace: ProductNamespace, key: string, workspaceId: string): void {
        const pending = this.pendingWrites.get(pendingKey(namespace, key, workspaceId))
        if (!pending || pending.flushing) return
        if (pending.timer) globalThis.clearTimeout(pending.timer)
        this.pendingWrites.delete(pendingKey(namespace, key, workspaceId))
        for (const waiter of pending.waiters) waiter.reject(new Error(`The ${namespace} record was deleted before its pending write completed`))
    }

    private async refetchRecord(namespace: ProductNamespace, key: string, workspaceId: string): Promise<ProductRecord | null> {
        const id = idFromKey(namespace, key)
        if (!id) return null
        const result = await retryBusy(() =>
            this.request("appData.get", {
                namespace,
                key,
                scope: { workspaceId },
            })
        )
        if (!result.found) {
            this.deleteLocalRecord(namespace, id)
            this.versions.delete(versionKey(namespace, key, workspaceId))
            return null
        }

        const parsed = recordForNamespace(namespace, result.value)
        if (!parsed || parsed.id !== id || recordWorkspaceId(parsed) !== workspaceId) return null
        if (isTaskRecord(parsed)) this.rememberTaskSource(parsed, result.value)
        this.setLocalRecord(namespace, parsed)
        if (result.version !== undefined) this.versions.set(versionKey(namespace, key, workspaceId), result.version)
        return parsed
    }

    private setLocalRecord(namespace: ProductNamespace, record: ProductRecord): void {
        if (namespace === REPOS_NAMESPACE) {
            if (isRepoRecord(record)) this.repos.set(record.id, record)
        } else {
            if (isTaskRecord(record)) this.tasks.set(record.id, record)
        }
    }

    private rememberTaskSource(record: TaskRecord, value: unknown): void {
        const key = taskSourceKey(record.id, record.workspaceId)
        const source = taskRecordWriteSource(value)
        if (source) {
            this.taskSources.set(key, source)
        } else {
            this.taskSources.delete(key)
        }
    }

    private deleteLocalRecord(namespace: ProductNamespace, id: string): void {
        if (namespace === REPOS_NAMESPACE) {
            this.repos.delete(id)
        } else {
            const existing = this.tasks.get(id)
            this.tasks.delete(id)
            if (existing) this.taskSources.delete(taskSourceKey(id, existing.workspaceId))
        }
    }
}
